import { ButtonHTMLAttributes } from 'react'

import Image from 'next/image'

import { imgProxyLoader } from '~/utils/imgproxy'

import { ImageFile } from '~/generated/graphql'
import Icon from '~/shared/atoms/Icon'

import { useCarousel } from './CarouselApi'
import {
  CarouselButtonsWrapper,
  CarouselImageWrapper,
  EmblaDot,
  EmblaDots,
  StyledCarouselButton,
  StyledCarouselContent,
  StyledCarouselItem,
} from './CarouselShards'

export type SliderImg = Pick<ImageFile, 'id' | 'original' | 'preview'>

function CarouselContent({ className, ...props }: React.ComponentProps<'div'>) {
  const { carouselRef } = useCarousel()

  return (
    <StyledCarouselContent ref={carouselRef} data-slot='carousel-content' className={className}>
      <div {...props} />
    </StyledCarouselContent>
  )
}

function CarouselItem({ ...props }: React.ComponentProps<'div'>) {
  return <StyledCarouselItem role='group' aria-roledescription='slide' data-slot='carousel-item' {...props} />
}

export type CarouselButtonProps = { isNext?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>

function BaseCarouselButton({ isNext = false, ...props }: CarouselButtonProps) {
  const { scrollPrev, scrollNext } = useCarousel()

  const onClick = isNext ? scrollNext : scrollPrev

  const dataSlot = isNext ? 'carousel-next' : 'carousel-previous'

  return <button data-slot={dataSlot} onClick={onClick} {...props} />
}

function CarouselButton({ className, isNext }: CarouselButtonProps) {
  const { canScrollPrev, canScrollNext, scrollSnaps } = useCarousel()
  const canScroll = isNext ? canScrollNext : canScrollPrev

  if (scrollSnaps.length <= 1) return null

  return (
    <StyledCarouselButton isNext={isNext} disabled={!canScroll} className={className}>
      <Icon icon='arrow' />
    </StyledCarouselButton>
  )
}

const maxDots = 5
function CarouselDots() {
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useCarousel()

  if (scrollSnaps.length <= 1) return null

  const lastSnapIndex = scrollSnaps.length - 1
  const dotsOnEachSide = Math.floor(maxDots / 2) // Number of dots to the left and right of the active dot (at maxDots: 2 on each side)

  let initialIndex = Math.max(0, selectedIndex - dotsOnEachSide) // Initial index of visible dots - shifted to the left of the active one
  const finalIndex = Math.min(lastSnapIndex, initialIndex + maxDots - 1) // Final index of visible dots, but must not exceed the end of the list

  // If the visible range is smaller than maxDots (e.g., at the end of the list), then move the start to the left
  if (finalIndex - initialIndex < maxDots - 1) {
    initialIndex = Math.max(0, finalIndex - maxDots + 1)
  }

  // Generate an array of indexes to represent the dots that should be visible
  const visibleDots = Array.from({ length: finalIndex - initialIndex + 1 }, (_, i) => initialIndex + i)

  const lastVisibleIndex = visibleDots.length - 1

  return (
    <EmblaDots>
      {visibleDots.map((index, i) => {
        const isSelected = index === selectedIndex
        const isEdge = i === 0 || i === lastVisibleIndex
        // isSmall if it's at the edge and  there are more slides ( left || right )
        const isSmall = isEdge && ((i === 0 && initialIndex > 0) || (i === lastVisibleIndex && finalIndex < lastSnapIndex))

        return (
          <EmblaDot
            key={index}
            selected={isSelected}
            isSmall={isSmall}
            onClick={e => {
              e.stopPropagation()
              onDotButtonClick(e, index)
            }}
          />
        )
      })}
    </EmblaDots>
  )
}

function CarouselControls({ Button = CarouselButton }: { Button?: React.ComponentType<CarouselButtonProps> }) {
  return (
    <>
      <CarouselButtonsWrapper>
        <Button />
        <Button isNext />
      </CarouselButtonsWrapper>
      <CarouselDots />
    </>
  )
}

function CarouselImage({ src, alt, index = 0 }: { src: string; alt: string; index: number }) {
  return (
    <CarouselImageWrapper>
      <Image
        loader={imgProxyLoader}
        src={src}
        quality={80}
        alt={alt}
        layout='fill'
        objectFit='cover'
        priority={index === 0}
        loading={index === 0 ? 'eager' : 'lazy'}
      />
    </CarouselImageWrapper>
  )
}

export { BaseCarouselButton, CarouselButton, CarouselContent, CarouselControls, CarouselDots, CarouselImage, CarouselItem }
